<template>
  <b-form>
    <div class="d-flex justify-content-between mt-5 mb-3">
      <h3>
        <b>Account Details</b>
      </h3>
      <div class="text-right mt-2">
        <div v-if="editing">
          <b-link class="buttontext-2" @click="resetForm">Cancel</b-link>
          <b-link class="buttontext ml-4" @click="onSubmit">Save</b-link>
        </div>
        <b-link v-else @click="onClickUpdate">
          <b-img
            width="16"
            class="rounded-0 mr-2 mb-1"
            :src="require('@/assets/icons/edit.svg')"
          ></b-img>
          <span class="buttontext">Update</span>
        </b-link>
      </div>
    </div>

    <b-row :class="isMobile ? 'd-block' : ''">
      <b-col>
        <b-form-group id="input-group-full-name" class="col-12">
          <i
            class="clearable"
            v-show="form.full_name && editing"
            @click="form.full_name = ''"
            >&times;</i
          >

          <b-form-input
            id="input-full-name"
            ref="refFullName"
            v-model="form.full_name"
            class="input-effect"
            :class="form.full_name ? 'has-content' : ''"
            :disabled="!editing"
            type="text"
            required
            aria-describedby="input-full-name-feedback"
          ></b-form-input>
          <label>Full name</label>

          <div class="input-error" v-if="form.$getError('full_name')">
            {{ form.$getError('full_name')[0] }}
          </div>
        </b-form-group>
      </b-col>

      <b-col>
        <b-form-group id="input-group-phone-number" class="col-12">
          <i
            class="clearable"
            v-show="form.business_contact_number && editing"
            @click="form.business_contact_number = ''"
            >&times;</i
          >

          <b-form-input
            id="input-phone-number"
            v-model="form.business_contact_number"
            class="input-effect"
            :class="form.business_contact_number ? 'has-content' : ''"
            :disabled="!editing"
            type="text"
            required
            aria-describedby="input-phone-number-feedback"
          ></b-form-input>
          <label>Business contact number</label>

          <div
            class="input-error"
            v-if="form.$getError('business_contact_number')"
          >
            {{ form.$getError('business_contact_number')[0] }}
          </div>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row :class="isMobile ? 'd-block' : ''">
      <b-col>
        <b-form-group id="input-group-business-name" class="col-12">
          <i
            class="clearable"
            v-show="form.business_name && editing"
            @click="form.business_name = ''"
            >&times;</i
          >

          <b-form-input
            id="input-business-name"
            v-model="form.business_name"
            class="input-effect"
            :class="form.business_name ? 'has-content' : ''"
            :disabled="!editing"
            type="text"
            required
            aria-describedby="input-business-name-feedback"
          ></b-form-input>
          <label>Business name</label>

          <div class="input-error" v-if="form.$getError('business_name')">
            {{ form.$getError('business_name')[0] }}
          </div>
        </b-form-group>
      </b-col>

      <b-col>
        <b-form-group id="input-group-address" class="col-12">
          <place-autocomplete
            v-model="form.business_address"
            name="location"
            :api-key="$env.placesApiKey"
            :input-state="null"
            :input-error="
              form.$getError('business_address')
                ? form.$getError('business_address')[0]
                : null
            "
            input-class="ellipsis w-100"
            :prepend-icon="true"
            :disabled="!editing"
          ></place-autocomplete>
        </b-form-group>
      </b-col>
    </b-row>

    <b-alert
      class="custom-alert"
      :show="dismissCountDown"
      dismissible
      fade
      variant="primary"
      @dismiss-count-down="countDownChanged"
      >Your profile has been updated</b-alert
    >
  </b-form>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import Form from '@/utils/form'
import PlaceAutocomplete from '@/components/autocomplete/PlaceAutocomplete'

export default {
  components: {
    PlaceAutocomplete
  },

  computed: {
    ...mapState({
      user: state => state.auth.user
    })
  },

  data() {
    return {
      loading: false,
      show: false,
      editing: false,
      dismissCountDown: 0,
      form: new Form({
        full_name: null,
        business_name: null,
        business_contact_number: null,
        business_address: null
      })
    }
  },

  methods: {
    ...mapActions({
      updateUser: 'auth/updateUser',
      getAuthenticatedUser: 'auth/getAuthenticatedUser'
    }),

    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },

    onClickUpdate() {
      this.editing = true
      this.$nextTick(() => {
        this.$refs.refFullName.focus()
      })
    },

    resetForm() {
      this.editing = false
      this.form.full_name = this.user.full_name || null
      this.form.business_name = this.user.business_name || null
      this.form.business_contact_number =
        this.user.business_contact_number || null
      this.form.business_address = this.user.business_address || null
      this.scrollToTop()
      this.form.$clearErrors()
    },

    async onSubmit() {
      this.loading = true
      await this.updateUser(this.form)
        .then(() => {
          this.editing = false
          this.scrollToTop()
          this.dismissCountDown = 3
          this.form.$clearErrors()
        })
        .catch(error => {
          this.form.$handleError(error)
        })
      this.loading = false
    }
  },

  created() {
    if (!this.user) {
      this.getAuthenticatedUser().then(() => this.resetForm())
    } else {
      this.resetForm()
    }
  }
}
</script>

<style lang="scss" scoped>
.form-control:disabled,
.form-control[readonly] {
  background-color: #f8f9fa;
}
.custom-select {
  background: none;
}
.append-icon {
  z-index: 1;
}
.buttontext {
  font-size: 14px;
  font-weight: bold;
}
.buttontext-2 {
  color: grey;
  font-size: 14px;
}
</style>
